import SelectInputFonts from "@builder/components/general/SelectInputFonts";
import availableFonts from "@builder/fonts/availableFonts";
import { addFontToAttributes } from "@builder/store/slices/BuilderSlice/builderSlice";
import { updateNodeProperty } from "../../../../store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import SidebarCollapse from "../../../app/SidebarCollapse";
import ColorInput from "../../../general/ColorInput";
import RadioInput from "../../../general/RadioInput";
import SelectInput from "../../../general/SelectInput";
import SliderInput from "../../../general/SliderInput/index";
import { getValueUnit } from "../../../general/editUtils";
import { EditableProps } from "../../editableProps";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const Typography = ({ parent, title, path, excluded, noWrapper, condition, stateMode, customLabel }: EditableProps) => {
    const node = useAppSelector(getSelectedNode);
    const activeNode = useAppSelector(state => state.builder.activeNode);
    const dispatch = useAppDispatch();
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const cvModeIndex = cvMode?.id === node?.id ? cvMode.index : null;
    const { t } = useTranslation();

    if (!node) return null;

    const style = getStyle(node, path, stateMode, cvModeIndex);
    const stylePath = getStylePath(path, stateMode, cvModeIndex);

    //Font Families
    const fontNamesObject: Record<string, string> = useMemo(
        () =>
            availableFonts.reduce((acc, font) => {
                if (font.name === "inherit") {
                    return { ...acc, [font.name]: t("generalComponents.selectInputFonts.inherit") };
                }
                return { ...acc, [font.name]: font.name };
            }, {}),
        [availableFonts]
    );

    //Color
    let colorElem = null;

    if (!excluded?.includes("color")) {
        const color = style.stateStyle?.color || style.default?.color || "";
        const colorCallback = (value: string) => {
            dispatch(updateNodeProperty({ propertyPath: [...stylePath, "color"], value }));
        };
        colorElem = <ColorInput title={customLabel || "Color"} color={color} callback={colorCallback} />;
    }

    //Text Alignment
    let textAlignElem = null;
    if (!excluded?.includes("textAlign")) {
        const textAlignment = style.stateStyle?.textAlign || style.default?.textAlign || "left";
        const alignmentOptions = [
            { value: "left", img: "/text-align-left.svg", checked: textAlignment === "left" },
            { value: "center", img: "/text-align-center.svg", checked: textAlignment === "center" },
            { value: "right", img: "/text-align-right.svg", checked: textAlignment === "right" },
            { value: "justify", img: "/text-align-justify.svg", checked: textAlignment === "justify" }
        ];
        const alignmentCallback = (value: string): void => {
            dispatch(updateNodeProperty({ propertyPath: [...stylePath, "textAlign"], value }));
        };
        textAlignElem = <RadioInput title={"Alignment"} groupName="textAlignment" options={alignmentOptions} callback={alignmentCallback} />;
    }

    const fontStyleCallback = (fontName: string) => {
        const selectedFont = availableFonts.find(font => font.name === fontName);
        if (!selectedFont)
            return {
                name: "Poppins"
            };
        dispatch(addFontToAttributes({ font: selectedFont }));
        dispatch(updateNodeProperty({ propertyPath: [...stylePath, "fontFamily"], value: fontName }));
    };

    //Font Size
    const fontSizeString = style.stateStyle?.fontSize || style.default?.fontSize;
    const fontSize = getValueUnit(fontSizeString);
    const fontSizeCallback = (value: string, unit: string): void => {
        dispatch(updateNodeProperty({ propertyPath: [...stylePath, "fontSize"], value: value + unit, remove: !value }));
    };

    //Font Weight
    const fontWeight = style.stateStyle?.fontWeight || style.default?.fontWeight || "300";
    const fontWeightOptions = {
        "300": "300",
        "400": "400",
        "500": "500",
        "600": "600",
        "700": "700",
        "800": "800"
    };
    const fontWeightCallback = (value: string): void => {
        dispatch(updateNodeProperty({ propertyPath: [...stylePath, "fontWeight"], value }));
    };

    if (condition && (!node || !condition(node, activeNode))) return null;

    if (noWrapper) {
        return (
            <>
                <SelectInputFonts
                    fonts={fontNamesObject}
                    callback={fontStyleCallback}
                    value={style.stateStyle?.fontFamily || style.default?.fontFamily || "Poppins"}
                    title="Text font"
                    layout="column"
                />
                <SliderInput title="Font Size" min={0} max={50} callback={fontSizeCallback} value={fontSize.value} unit={fontSize.unit} />
                <SelectInput title="Font Weight" value={fontWeight} options={fontWeightOptions} callback={fontWeightCallback} noTranslate />
                {textAlignElem}
                {colorElem}
            </>
        );
    } else {
        return (
            <SidebarCollapse title={title || "Typography"} parent={parent}>
                <SelectInputFonts
                    fonts={fontNamesObject}
                    callback={fontStyleCallback}
                    value={style.stateStyle?.fontFamily || style.default?.fontFamily || "Poppins"}
                    title="Text font"
                    layout="column"
                />
                <SliderInput title="Font Size" min={0} max={50} callback={fontSizeCallback} value={fontSize.value} unit={fontSize.unit} />
                <SelectInput title="Font Weight" value={fontWeight} options={fontWeightOptions} callback={fontWeightCallback} noTranslate />
                {textAlignElem}
                {colorElem}
            </SidebarCollapse>
        );
    }
};

export default Typography;
